import { font_title_secularOne } from "@/app/ui/fonts";
import { Button, ButtonProps } from "@/app/ui/forms/button";
import { DropdownMenuItem } from "@/app/ui/imported/dropdown-menu";
import { cn, youtube_getVideoLink } from "@/lib/utils";
import Link, { LinkProps } from "next/link";
import { HTMLAttributeAnchorTarget, ReactNode } from "react";
import { AiFillEdit, AiFillFolderOpen, AiFillIdcard, AiFillPlusCircle, AiFillYoutube } from "react-icons/ai";
import { IconType } from "react-icons/lib";
import { RiCheckboxMultipleBlankFill, RiScreenshot2Fill } from "react-icons/ri";
import { TbAlertTriangleFilled } from "react-icons/tb";


interface ButtonAddOptions extends ButtonProps {
    addField: "פרק" | "עונה" | "יוצר תוכן" | "פרויקט" | "טופס" | "סטטיסטיות",
    noSpan?: boolean
}


function BaseBtn({ colorScheme = "purple", children, variant = "outline", noSpan, ...props }: ButtonProps & { noSpan?: boolean }) {
    return <Button
        { ...props }
        variant={ variant }
        colorScheme={ colorScheme }
        className={ cn("min-w-fit w-11/12 sm:w-1/4",
            noSpan && "p-1",
            font_title_secularOne.className, props.className,) }>
        <div className={ cn("flex gap-1 items-center", noSpan && "[&_:not(:first-child)]:hidden") }>
            { children }
        </div>
    </Button>
}

export const ButtonAddX_icon = AiFillPlusCircle;
export function ButtonAddX({ addField, colorScheme = "green", ...props }: ButtonAddOptions) {
    return (
        <BaseBtn colorScheme={ colorScheme } { ...props }>
            <ButtonAddX_icon />
            <span>
                הוסף { addField }
            </span>
        </BaseBtn>
    )
}

export const ButtonEditX_icon = AiFillEdit;
export function ButtonEditX({ addField, colorScheme = "orange", ...props }: ButtonAddOptions) {
    return (
        <BaseBtn colorScheme={ colorScheme } { ...props }>
            <ButtonEditX_icon />
            <span>
                עדכן { addField }
            </span>
        </BaseBtn>
    )
}

export const ButtonDeleteX_icon = TbAlertTriangleFilled;
export function ButtonDeleteX({ addField, ...props }: ButtonAddOptions) {
    return (
        <BaseBtn variant={ "destructive" } colorScheme={ "none" } { ...props }>
            <ButtonDeleteX_icon />
            <span>
                מחיקת { addField }
            </span>
        </BaseBtn>
    )
}

export const ButtonProjects_icon = RiScreenshot2Fill;
export function ButtonProjects({ ...props }: ButtonProps) {
    return (
        <BaseBtn colorScheme={ "violet" } { ...props }>
            <ButtonProjects_icon />
            <span>
                { props.children }
            </span>
        </BaseBtn>
    )
}

/**
 * DropDown BUTTONS
 */

interface BaseDropDownProps extends LinkProps {
    ReactIcon: IconType,
    className?: string,
    children: ReactNode,
    target?: HTMLAttributeAnchorTarget
}

function BaseDropDown({ className, ReactIcon, children, ...props }: BaseDropDownProps) {
    const scrollToTop = !props?.href?.toString().startsWith("?")
    return <DropdownMenuItem className={ className } asChild>
        <Link className="flex gap-2 my-1" scroll={ scrollToTop } { ...props }>
            { <ReactIcon size={ "1.5em" } /> }
            <span className="flex-1 text-center font-black">
                { children }
            </span>
        </Link>
    </DropdownMenuItem>
}

export function DropdownMenuItem_Delete({ id, alternateParam = "delete" }: { id: string, alternateParam?: "delete" | string }) {
    return <BaseDropDown
        className="bg-red-700 hover:bg-red-300 focus:bg-red-300 hover:text-black"
        href={ `?${alternateParam}=${id}` }
        replace
        ReactIcon={ ButtonDeleteX_icon }>
        מחיקה
    </BaseDropDown>
}

export function DropdownMenuItem_Edit({ id }: { id: string }) {
    return <BaseDropDown
        href={ `?edit=${id}` }
        ReactIcon={ ButtonEditX_icon }>
        עריכה
    </BaseDropDown>
}

export function DropdownMenuItem_OpenAtYoutube({ id, type }: { id: string, type: "channel" | "video" }) {
    const url = type == "video" ? youtube_getVideoLink(id) : `https://www.youtube.com/channel/${id}`
    return <BaseDropDown
        target="_blank"
        href={ url }
        ReactIcon={ AiFillYoutube }>
        פתיחה ביוטיוב
    </BaseDropDown>
}

export function DropdownMenuItem_ShowInSite({ url }: { url: string }) {
    return <BaseDropDown
        target="_blank"
        href={ url }
        ReactIcon={ AiFillIdcard }>
        הצג באתר
    </BaseDropDown>
}

export function DropdownMenuItem_ShowVideos({ url }: { url: string }) {
    return <BaseDropDown
        className="bg-nfPurple-400/60 hover:bg-nfPurple-400 focus:bg-nfPurple-400"
        href={ url }
        ReactIcon={ RiCheckboxMultipleBlankFill }>
        תצוגת פרקים
    </BaseDropDown>
}

export function DropdownMenuItem_Open({ id }: { id: string }) {
    return <BaseDropDown
        className="bg-nfPurple-400/60 hover:bg-nfPurple-400 focus:bg-nfPurple-400"
        href={ `?show=${id}` }
        ReactIcon={ AiFillFolderOpen }>
        הצג
    </BaseDropDown>
}